import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth.jsx';
import { FiUser, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';
import BullseyeIcon from '../components/common/icons/BullseyeIcon';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast.error('Vyplňte všetky polia');
      return;
    }

    setIsLoading(true);
    
    try {
      await login(email, password);
      toast.success('Úspešne prihlásený!');
    } catch (error) {
      // Lepšia error správa podľa typu chyby
      let errorMessage = 'Chyba pri prihlásení';
      
      if (error.name === 'ConfigurationError') {
        errorMessage = error.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.status === 400) {
        errorMessage = 'Chyba prihlásenia. Skontrolujte konfiguráciu Supabase.';
      }
      
      console.error('Login error:', error);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center p-4 text-slate-100">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-6xl mb-4 flex justify-center"
          >
            <BullseyeIcon className="text-5xl text-primary-300 drop-shadow-lg" aria-hidden="true" />
          </motion.div>
          <h1 className="text-3xl font-bold text-primary-200 mb-2">
            Administrácia Tomboly
          </h1>
          <p className="text-slate-300">
            Slovenský zväz posunkujúcich
          </p>
        </div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-900/90 rounded-2xl shadow-lg border border-slate-800 p-8 backdrop-blur"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-slate-200 mb-2">
                <FiUser className="inline w-4 h-4 mr-1" />
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="admin@posunkujuci.com"
                required
                disabled={isLoading}
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-slate-200 mb-2">
                <FiLock className="inline w-4 h-4 mr-1" />
                Heslo
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-12"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-primary-200 transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-100"></div>
                  Prihlasovanie...
                </div>
              ) : (
                'Prihlásiť sa'
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 text-sm text-slate-400"
        >
          <p>© 2025 Slovenský zväz posunkujúcich</p>
          <p>Zabezpečené prihlásenie</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LoginPage;